/**
 * Technology Asset Cell Renderer
 *
 * Combines IP Address and Domain Name into a single Asset column.
 * Shows domain first (asset.group) with IP as secondary info, or just IP if no domain.
 *
 * Pattern matches AssetNameCell but optimized for technology drawer context where
 * the relationship between domain and IP is critical for understanding where the
 * technology is deployed.
 */

import React from 'react';

import { NameColumn } from '@/components/NameColumn';
import type { Asset } from '@/types';

interface TechnologyAssetCellProps {
  asset: Asset;
}

/**
 * Memoized cell renderer for technology asset column
 *
 * Display priority:
 * - Primary: Domain name (asset.group) if available, otherwise IP address (asset.identifier)
 * - Secondary: IP address (asset.identifier) if domain exists
 *
 * @param asset - The asset to render
 * @returns NameColumn component with domain/IP and appropriate fallbacks
 */
export const TechnologyAssetCell: React.FC<TechnologyAssetCellProps> =
  React.memo(({ asset }) => {
    // Primary display: domain (group) or IP (identifier)
    const displayName = asset.group || asset.identifier || '-';

    // Secondary display: IP if domain exists
    const description = asset.group ? asset.identifier : undefined;

    return <NameColumn name={displayName} description={description} />;
  });

TechnologyAssetCell.displayName = 'TechnologyAssetCell';
